import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, ChevronRight } from "lucide-react";
import { useRestaurants } from "../../hooks/useRestaurants";
import { useStoreOffers } from "../../hooks/useOffers";
import toast from "react-hot-toast";

import { Restaurant } from "../../types/restaurant";
import { StoreOffer } from "../../types/offer";

const PayBill = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storeID = searchParams.get("storeID");
    const storeType = searchParams.get("storeType");

    const { data: restaurants = [], isLoading } = useRestaurants() as { data: Restaurant[], isLoading: boolean };
    const { data: storeOffers = [], isLoading: isLoadingOffers } = useStoreOffers(storeID);
    const restaurant = restaurants.find((r: Restaurant) => r.dbID === storeID);

    const [amount, setAmount] = useState("");
    const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
    const [phone, setPhone] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);

    const [step, setStep] = useState<1 | 2>(1);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleProceed = () => {
        const numAmount = Number(amount);
        if (!amount || isNaN(numAmount) || numAmount <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }
        setStep(2);
    };

    const handleFinalPay = async () => {
        if (!phone || phone.length < 10) {
            toast.error("Please enter a valid 10-digit mobile number");
            return;
        }

        setIsProcessing(true);
        try {
            const amountStr = finalAmount.toString();
            const productInfo = "Dining Bill Payment";
            const firstName = "Guest";
            const email = "guest@thediningapp.com";
            const txnId = Date.now().toString();

            const response = await fetch(
                // "http://127.0.0.1:5001/thediningapp-a976c/us-central1/payuHashFunction",
                "https://payuhashfunction-jdoxig6lva-uc.a.run.app",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        amount: amountStr,
                        productInfo,
                        firstName,
                        email,
                        phone,
                        txnId,
                        paymentType: "BILL_PAYMENT",
                        storeId: restaurant?.dbID,
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log("PayU response data received");

                // the Cloud Function returns a self-submitting form
                document.open();
                document.write(data.html);
                document.close();
            } else {
                toast.error("Unable to initiate transaction. Please try later.");
                setIsProcessing(false);
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong. Please try again.");
            setIsProcessing(false);
        }
    };

    const baseAmount = Number(amount) || 0;

    // Default to first offer if none selected and offers exist
    useEffect(() => {
        if (!selectedOfferId && storeOffers.length > 0) {
            setSelectedOfferId(storeOffers[0].id);
        }
    }, [storeOffers, selectedOfferId]);

    const selectedOffer = storeOffers.find(o => o.id === selectedOfferId);

    let discountAmount = 0;
    if (selectedOffer) {
        const discountPercent = parseFloat(selectedOffer.discount) || 0;
        const minOrder = parseFloat(selectedOffer.minimumOrderValue) || 0;
        const maxDiscount = parseFloat(selectedOffer.maximumDiscountValue) || 0;

        if (baseAmount >= minOrder) {
            discountAmount = Math.round(baseAmount * (discountPercent / 100));
            if (maxDiscount > 0 && discountAmount > maxDiscount) {
                discountAmount = maxDiscount;
            }
        }
    }

    const amountAfterDiscount = baseAmount - discountAmount;

    const hasConvenienceFee = restaurant?.isConvenienceFee === true || restaurant?.isConvenienceFee === "true";
    const hasServiceFee = restaurant?.isServiceFee === true || restaurant?.isServiceFee === "true";

    const convenienceFee = hasConvenienceFee ? Math.round(amountAfterDiscount * 0.05) : 0;
    const serviceFee = hasServiceFee ? Math.round(amountAfterDiscount * 0.05) : 0;

    const finalAmount = amountAfterDiscount + convenienceFee + serviceFee;

    console.log({ storeOffers, restaurant })

    if (step === 2) {
        return (
            <div className="fixed inset-0 bg-[#0e0e0e] text-white flex flex-col font-sans overflow-hidden">
                <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-6 bg-[#0e0e0e] z-50">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setStep(1)} className="p-1">
                            <ArrowLeft className="text-white" size={26} />
                        </button>
                        <div>
                            <h1 className="text-[20px] font-bold text-white leading-tight tracking-tight">
                                {restaurant?.name || "Hard Rock Cafe"}
                            </h1>
                            <p className="text-[#8e8e8e] text-[14px] font-medium mt-0.5">
                                {restaurant?.area || "Banjara Hills"}, {restaurant?.city || "Hyderabad"}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar px-4 pb-48">
                    <div className="flex justify-between items-center mt-2 mb-4 px-1">
                        <h2 className="text-[17.5px] font-bold text-white tracking-wide">Your bill amount</h2>
                        <button
                            onClick={() => setStep(1)}
                            className="text-white text-[14px] font-semibold tracking-wide border-b border-dashed border-white pb-0.5 leading-none hover:opacity-80 transition-opacity"
                        >
                            Edit
                        </button>
                    </div>

                    {/* Amount Card */}
                    <div className="rounded-[18px] overflow-hidden bg-[#161616] border border-white/[0.08] mb-8 shadow-sm">
                        <div className="bg-[#6b4cff] px-4 py-3">
                            <p className="text-white text-[13.5px] font-medium tracking-wide">
                                {discountAmount > 0 ? `₹${discountAmount} saved on this bill + ₹9 cashback` : "Secure payment + ₹9 cashback"}
                            </p>
                        </div>
                        <div className="p-5 py-6 flex flex-col gap-1">
                            <div className="flex items-center gap-3">
                                <span className="text-[36px] font-bold text-white tracking-tight leading-none">₹{finalAmount}</span>
                                {discountAmount > 0 && (
                                    <span className="text-[20px] font-medium text-[#777] line-through decoration-[#777] decoration-[1.5px]">₹{amount}</span>
                                )}
                            </div>
                            {(hasConvenienceFee || hasServiceFee) && (
                                <p className="text-[#888] text-[12px] font-medium">Incl. of taxes and fees</p>
                            )}
                        </div>
                    </div>

                    <h2 className="text-[17.5px] font-bold text-white mb-4 px-1 tracking-wide">Savings corner</h2>

                    {selectedOffer ? (
                        <div className="rounded-[18px] bg-[#161616] border border-white/[0.08] p-4 py-5 flex items-center justify-between mb-5 shadow-sm">
                            <div className="flex items-center gap-3.5">
                                <div className="w-10 h-10 rounded-full bg-[#111424] border border-blue-900/50 flex items-center justify-center shrink-0">
                                    <div className="text-red-500 font-extrabold text-[15px] italic font-serif">∞</div>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-white text-[14px] font-semibold leading-[1.3] mb-1">
                                        {selectedOffer.title}
                                    </h3>
                                    <p className="text-[#888] text-[12px] font-medium">with code {selectedOffer.offerCode}</p>
                                </div>
                            </div>
                            <div className="px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500 text-[12px] font-bold">
                                Applied
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-[18px] bg-[#161616] border border-white/[0.08] p-4 py-5 flex items-center justify-between mb-5 shadow-sm opacity-50">
                            <p className="text-gray-400 text-sm">Select an offer below to save more</p>
                        </div>
                    )}

                    <div className="flex justify-center mb-10 mt-2">
                        <button className="text-white text-[14px] font-semibold flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                            View all offers <ChevronRight size={16} strokeWidth={2.5} />
                        </button>
                    </div>

                    <h2 className="text-[17.5px] font-bold text-white mb-4 px-1 tracking-wide">Enter Mobile Number</h2>
                    <div className="rounded-[18px] bg-[#161616] border border-white/[0.08] p-4 flex flex-col shadow-sm mb-6">
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="10-digit mobile number"
                            className="bg-transparent border-none text-white focus:outline-none text-[16px] placeholder:text-gray-600 w-full tracking-wider"
                            maxLength={10}
                        />
                    </div>

                    <h2 className="text-[17.5px] font-bold text-white mb-4 px-1 tracking-wide">Bill Summary</h2>

                    <div className="rounded-[18px] bg-[#161616] border border-white/[0.08] p-5 flex flex-col gap-4 shadow-sm mb-4">
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-[#d1d1d1] text-[14.5px] font-medium tracking-wide">Bill amount</span>
                            <span className="text-white text-[14.5px] font-medium tracking-wide">₹{amount}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#a5b4fc] text-[14.5px] font-medium tracking-wide">
                                {selectedOffer?.title || "Discount"}
                            </span>
                            <span className="text-[#a5b4fc] text-[14.5px] font-medium tracking-wide">-₹{discountAmount}</span>
                        </div>
                        {hasConvenienceFee && (
                            <div className="flex justify-between items-center">
                                <span className="text-[#d1d1d1] text-[14.5px] font-medium tracking-wide">Convenience Fee (5%)</span>
                                <span className="text-white text-[14.5px] font-medium tracking-wide">₹{convenienceFee}</span>
                            </div>
                        )}
                        {hasServiceFee && (
                            <div className="flex justify-between items-center">
                                <span className="text-[#d1d1d1] text-[14.5px] font-medium tracking-wide">Service Fee (5%)</span>
                                <span className="text-white text-[14.5px] font-medium tracking-wide">₹{serviceFee}</span>
                            </div>
                        )}
                        <div className="border-b border-white/[0.06] pb-1"></div>
                        <div className="flex justify-between items-start pt-1">
                            <span className="text-white text-[16.5px] font-bold tracking-wide mt-1">To be paid</span>
                            <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-2">
                                    {finalAmount !== Number(amount) && (
                                        <span className="text-[#777] text-[14.5px] font-medium line-through decoration-[1.5px]">₹{amount}</span>
                                    )}
                                    <span className="text-white text-[18px] font-bold">₹{finalAmount}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <span className="text-[#a5b4fc] text-[11px] font-bold mt-0.5 px-2 py-1 bg-[#1a1b3a] rounded-[6px]">
                                        You saved ₹{discountAmount}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="absolute bottom-0 left-0 right-0 bg-[#0e0e0e] z-50 rounded-t-[20px] shadow-[0_-10px_30px_rgba(0,0,0,0.8)] border border-white/[0.08] border-b-0">

                    <div className="p-4 px-4">
                        <button
                            onClick={handleFinalPay}
                            disabled={isProcessing}
                            className="w-full h-[46px] bg-white text-black hover:bg-gray-100 disabled:bg-gray-400 rounded-xl font-bold text-[17px] tracking-wide transition-all duration-300 shadow-xl active:scale-[0.98] flex items-center justify-center relative"
                        >
                            {isProcessing ? "Processing..." : "Proceed to Pay"}
                            {!isProcessing && (
                                <div className="absolute right-5 w-0 h-0 border-t-[5px] border-t-transparent border-l-[7px] border-l-black border-b-[5px] border-b-transparent"></div>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Step 1 UI
    return (
        <div className="fixed inset-0 bg-black text-white flex flex-col font-sans overflow-hidden">
            <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-6 bg-black z-50">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-1">
                        <ArrowLeft className="text-white" size={26} />
                    </button>
                    <div>
                        <h1 className="text-[20px] font-bold text-white leading-tight tracking-tight">
                            {restaurant?.name || "Hard Rock Cafe"}
                        </h1>
                        <p className="text-[#8e8e8e] text-[14px] font-medium mt-0.5">
                            {restaurant?.area || "Banjara Hills"}, {restaurant?.city || "Hyderabad"}
                        </p>
                    </div>
                </div>

            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 pb-32">
                <h2 className="text-[17px] font-bold text-white mt-4 mb-4">Enter your bill amount</h2>

                {/* Main Amount Card */}
                <div className="rounded-[24px] overflow-hidden bg-gradient-to-r from-[#1b134d]  to-[#2e1d9e]">
                    <div className="p-5 flex items-baseline bg-black border-2 border-white/50 rounded-[24px]">
                        <span className="text-4xl font-semibold text-white mr-1.5 opacity-90">₹</span>
                        <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full bg-transparent border-none text-white focus:outline-none text-4xl font-semibold leading-none placeholder:text-gray-900 tracking-tight"
                            placeholder="0"
                        />
                    </div>
                    <div className="px-5 py-3.5 flex justify-between items-center cursor-pointer ">
                        <p className="text-white text-[13.5px] font-medium">Extra 10% cashback on your dining bill</p>
                        <ChevronRight size={18} className="text-white" />
                    </div>
                </div>

                {/* Available Offers */}
                <h2 className="text-[17px] font-bold text-white mt-12 mb-5">Available Offers</h2>

                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-4">
                    {isLoadingOffers ? (
                        <p className="text-gray-500 text-sm animate-pulse px-1">Loading offers...</p>
                    ) : storeOffers.length === 0 ? (
                        <p className="text-gray-500 text-sm px-1">No offers available for this store (ID: {storeID?.substring(0, 8)}...)</p>
                    ) : (
                        storeOffers.map((offer) => {
                            const isSelected = selectedOfferId === offer.id;

                            if (offer.type === 'PAYBILL' || offer.type === 'BOTH') {
                                return (
                                    <div
                                        key={offer.id}
                                        onClick={() => setSelectedOfferId(offer.id)}
                                        className={`min-w-fit rounded-lg overflow-hidden border transition-all duration-300 cursor-pointer ${isSelected ? 'bg-[#231E3D] border-none' : 'bg-[#111111] border-white/[0.05]'
                                            }`}
                                    >
                                        <div className={`relative px-3 pr-8 py-2 flex items-center transition-colors ${isSelected ? 'bg-[#5c42ff]' : 'bg-[#1c1c1f]'
                                            }`}>
                                            <h3 className="text-white text-lg relative z-10 leading-tight w-fit whitespace-nowrap">{offer.title}</h3>
                                            <div className="absolute right-[-2px] bottom-[-2px] opacity-20 -rotate-12 font-black text-3xl select-none">%</div>
                                        </div>
                                        <div className={`p-3 py-2 ${isSelected ? 'bg-[#201c39]' : 'bg-[#2d2d30]'}`}>
                                            <p className={`${isSelected ? 'text-[#757e96]' : 'text-[#bebcbc]'} text-base whitespace-nowrap leading-tight`}>
                                                {offer.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={offer.id}
                                    onClick={() => setSelectedOfferId(offer.id)}
                                    className={`shrink-0 w-[240px] rounded-md p-4 flex flex-col justify-between min-h-[96px] border transition-all duration-300 cursor-pointer ${isSelected ? 'bg-[#1a1a1f] border-[#5c42ff]/50 ring-1 ring-[#5c42ff]/30' : 'bg-[#111111] border-white/[0.05]'
                                        }`}
                                >
                                    <h3 className={`${isSelected ? 'text-white' : 'text-[#efefef]'} font-medium text-[13.5px] leading-tight`}>
                                        {offer.title}
                                    </h3>
                                    <p className={`${isSelected ? 'text-[#999]' : 'text-[#888888]'} text-[12px] font-medium`}>
                                        {offer.description}
                                    </p>
                                </div>
                            );
                        })
                    )}
                </div>


            </div>

            {/* Fixed Footer Proceed Button */}
            <div className="absolute rounded-t-[20px] shadow-[0_-10px_30px_rgba(0,0,0,0.8)] border border-white/[0.08]  bottom-0 left-0 right-0 p-4 bg-black/80 backdrop-blur-md z-50">
                <button
                    onClick={handleProceed}
                    className="w-full h-[45px] bg-white text-black hover:bg-gray-100 disabled:bg-gray-600 disabled:text-gray-400 rounded-xl font-bold text-[17px] transition-all duration-300 shadow-2xl active:scale-[0.98]"
                >
                    Proceed
                </button>
            </div>
        </div>
    );
};

export default PayBill;
